/**
 * Trailblazers treasure distribution — spreading a reward pool across the map.
 *
 * A treasure item's quality tier biases WHERE it tends to land. Rounds in
 * Trailblazers are not authored; they are derived topology depths on the node
 * graph (see ./trlTopology `deriveNodeLevels`), so "the 5th round" simply means
 * "nodes at derived level 5" and the deepest level is the round count.
 *
 * For each tier we place a gaussian over that round axis:
 *
 *   low  → centred on round 1          (early)
 *   mid  → centred on the middle round
 *   high → centred on the final round  (late)
 *
 * A node at round L gets weight `exp(-(L - mu)² / 2σ²)`, where σ is the admin's
 * per-tier spread. Small σ clusters hard on the target round; large σ flattens
 * toward uniform, which is how an admin opts out of the bias. It is a bias, not
 * a guarantee — a "high" item can still land early, just rarely.
 *
 * Everything here is pure and the RNG is injectable, so the admin UI, the server
 * and the tests all agree on the same rules.
 */
import { TRL_TREASURE_MIN_SPREAD } from '../types/trlTreasure.js';
/**
 * A node is eligible for treasure only once it sits in the round graph.
 *
 * Level 0 / undefined means the node is unconnected (or caught in a cycle) and
 * has no round, so there is no meaningful place for it on the curve — and no
 * team can reach it either.
 */
function isEligible(node) {
    return typeof node.derivedLevel === 'number' && node.derivedLevel >= 1;
}
/**
 * The round a tier aims for, given a map `maxLevel` rounds deep.
 *
 * Exported so the admin UI can draw the same curve it is about to roll against.
 */
export function trlTreasureTierTargetRound(tier, maxLevel) {
    switch (tier) {
        case 'low':
            return 1;
        case 'high':
            return maxLevel;
        case 'mid':
            return (1 + maxLevel) / 2;
    }
}
/**
 * Un-normalised probability that a tier places into a given round.
 *
 * Exported for the admin UI's curve preview, so what an admin sees is literally
 * the weighting the roll uses.
 */
export function trlTreasureRoundWeight(tier, round, maxLevel, tierSpread) {
    const mu = trlTreasureTierTargetRound(tier, maxLevel);
    const sigma = Math.max(TRL_TREASURE_MIN_SPREAD, tierSpread[tier]);
    const delta = round - mu;
    return Math.exp(-(delta * delta) / (2 * sigma * sigma));
}
/**
 * Per-round placement probabilities for a tier, normalised to sum to 1 over the
 * rounds that actually have nodes. Index 0 is round 1.
 *
 * Rounds are weighted by how many nodes they hold, matching the real roll: a
 * round with four nodes gets four chances at the same per-node weight.
 */
export function trlTreasureRoundProbabilities(tier, nodes, tierSpread) {
    const eligible = nodes.filter(isEligible);
    if (eligible.length === 0)
        return [];
    const maxLevel = Math.max(...eligible.map((n) => n.derivedLevel));
    const countByRound = new Array(maxLevel).fill(0);
    for (const node of eligible) {
        countByRound[node.derivedLevel - 1] += 1;
    }
    const weights = countByRound.map((count, i) => count * trlTreasureRoundWeight(tier, i + 1, maxLevel, tierSpread));
    const total = weights.reduce((sum, w) => sum + w, 0);
    if (total <= 0) {
        // Degenerate (every weight underflowed): fall back to uniform over
        // populated rounds so the preview never renders as all-zero.
        const populated = countByRound.filter((c) => c > 0).length;
        return countByRound.map((c) => (c > 0 ? 1 / populated : 0));
    }
    return weights.map((w) => w / total);
}
/** Pick an index from `weights` proportionally. Returns -1 if nothing is pickable. */
function weightedPick(weights, rng) {
    const total = weights.reduce((sum, w) => sum + w, 0);
    if (!(total > 0))
        return -1;
    let roll = rng() * total;
    for (let i = 0; i < weights.length; i++) {
        roll -= weights[i];
        if (roll <= 0)
            return i;
    }
    return weights.length - 1; // floating-point slack
}
/** Fisher-Yates, on a copy. */
function shuffled(values, rng) {
    const out = values.slice();
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
/**
 * Randomly place every copy of every treasure item across the map's nodes.
 *
 * Nodes may hold more than one treasure, so a node is never removed from the
 * pool once used — but its weight is divided by `1 + (treasures already there)`,
 * which spreads placements out naturally while still allowing a rich node.
 *
 * Returns a fresh `nodeId → itemIds[]` map; nodes with no treasure are absent.
 * Deterministic for a given `rng`.
 */
export function distributeTrlTreasure(items, nodes, tierSpread, rng = Math.random) {
    const eligible = nodes.filter(isEligible);
    if (eligible.length === 0 || items.length === 0)
        return {};
    const maxLevel = Math.max(...eligible.map((n) => n.derivedLevel));
    // Base weight per (tier, node) — the gaussian, before crowding is applied.
    const baseWeights = new Map();
    const weightsFor = (tier) => {
        let w = baseWeights.get(tier);
        if (!w) {
            w = eligible.map((n) => trlTreasureRoundWeight(tier, n.derivedLevel, maxLevel, tierSpread));
            baseWeights.set(tier, w);
        }
        return w;
    };
    // Expand quantities into individual placements, then shuffle so equal-weight
    // ties aren't silently resolved by the order of the admin's list.
    const placements = [];
    for (const item of items) {
        for (let i = 0; i < Math.max(1, item.quantity); i++)
            placements.push(item);
    }
    const assignments = {};
    const usedCount = new Array(eligible.length).fill(0);
    for (const item of shuffled(placements, rng)) {
        const base = weightsFor(item.tier);
        const weights = base.map((w, i) => w / (1 + usedCount[i]));
        const idx = weightedPick(weights, rng);
        if (idx < 0)
            continue;
        usedCount[idx] += 1;
        const nodeId = eligible[idx].id;
        (assignments[nodeId] ?? (assignments[nodeId] = [])).push(item.id);
    }
    return assignments;
}
